import {SvgView} from "./SvgView";
import {padLeft} from "../lib/stringFunctions";
import {ContainerView} from "./Containers";
import {PositionDataAdapter} from "../dashboard/PositionDataSupplier";
import {addSuffixToText} from "../lib/formatTool";
import {ContainerState, ContainerTypeValues} from "../dto/com.rico.sb2.entity.detail";
import {msToDurationMS} from "../lib/timeFunctions";
import {getCurrentTime} from "../dto/com.rico.sb2.service.timeprovider";
import {ContainerIconCache} from "./ContainerIconCache";

function secToMilli(sec: number) {
	return sec * 1000
}

function milliToSec(milli: number) {
	return Math.round(milli / 1000);
}

export interface PositionViewState {
	readonly data: PositionDataAdapter
	active: boolean
	enabled: boolean
	locked: boolean
	timeLeft: number | null
}

const containerIconCache = new ContainerIconCache();

export class PositionView extends SvgView implements PositionViewState {
	readonly data: PositionDataAdapter
	active = false
	timeTill: number | null = null
	private _container: ContainerView | null = null

	private iconBoundType: string | null = null
	private iconBoundSvg: SVGElement | null = null

	private clockBoundValue: string | null = null
	private currentString: string | null = null
	private voltageString: string | null = null

	lineY: number = 0

	/**
	 * Значение enabled, для которого этот отсортирован по глубине.
	 */
	activeByZOrder = true;

	constructor(data: PositionDataAdapter, view: SVGElement | null) {
		super(view)
		this.data = data
		this.bind('id', data.id.toString());
		this.update()
	}

	get container(): ContainerView | null {
		return this._container;
	}

	set container(containerView: ContainerView | null) {
		this._container = containerView;
		this.update();
	}

	get enabled(): boolean {
		return this.data.enabled
	}

	get locked(): boolean {
		return this.data.locked;
	}

	set enabled(v: boolean) {
		this.data.enabled = v
	}

	get timeLeft(): number | null {
		if (this.timeTill == null) return null
		return milliToSec(this.timeTill - getCurrentTime())
	}

	set timeLeft(n: number | null) {
		if (n == null) {
			this.timeTill = null
			return
		}
		this.timeTill = new Date(getCurrentTime() + secToMilli(n)).getTime()
	}

	get timeWait(): number | null {
		if (this.container == null) return null

		const containerData = this.container.data;
		if (containerData.state == null) return null;
		switch (containerData.state) {
			case ContainerState.UNLOADING:
			case ContainerState.WAITING:
			case ContainerState.FINISHED:
				if (containerData.programFinishTime == null) return null;
				return Math.round((getCurrentTime() - containerData.programFinishTime) / 1000);
			default:
				return null;
		}
	}

	update() {
		this.toggleClass('enabled', this.enabled)
		this.toggleClass('active', this.active)
		this.toggleClass('locked', this.locked)
		this.toggleClass('OCCUPIED', this._container != null);

		ContainerTypeValues.forEach(enumValue => this.toggleClass(enumValue, this.data.type == enumValue));

		// set type icon
		if (this.iconBoundType != this.data.type) {
			this.iconBoundSvg?.remove();
			this.iconBoundSvg = null;

			this.iconBoundType = this.data.type;
			const iconBoundSvg = containerIconCache.getIcon(this.data.type)
			if (iconBoundSvg) {
				this.iconBoundSvg = iconBoundSvg
				this.bindWith('containerTypeIcon', svg => svg.append(iconBoundSvg))
			}
		}

		this.updateText()
	}

	updateText() {
		function formatDurationMilli(milli: number) {
			const {minutes, seconds, forward} = msToDurationMS(milli);
			if (minutes > 999) {
				const hours = Math.floor(minutes / 60);
				return (forward ? '' : '-') + padLeft(Math.min(999, hours).toString(), 2, '0') + 'ч'
			}
			if (minutes > 99) {
				return (forward ? '' : '-') + padLeft(Math.min(999, minutes).toString(), 2, '0') + 'м'
			}
			return (forward ? '' : '-') + padLeft(minutes.toString(), 2, '0') + ':' + padLeft(seconds.toString(), 2, '0')
		}

		let clockBoundValue = ''
		if (this.timeLeft != null) {
			clockBoundValue = formatDurationMilli(this.timeLeft * 1000)
		} else if (this.timeWait != null) {
			clockBoundValue = formatDurationMilli(this.timeWait * 1000)
		}

		this.toggleClass('clock-on', clockBoundValue.length > 0);

		if (clockBoundValue != this.clockBoundValue) {
			this.clockBoundValue = clockBoundValue
			this.bind('clock', clockBoundValue)
		}

		let currentString = addSuffixToText(zeroIfEmpty(this.data.currentString), 'А')
		if (currentString != this.currentString) {
			this.currentString = currentString
			this.bind('current', currentString)
		}

		let voltageString = addSuffixToText(zeroIfEmpty(this.data.voltageString), 'V')
		if (voltageString != this.voltageString) {
			this.voltageString = voltageString
			this.bind('voltage', voltageString)
		}
	}

	tick() {
		if (this.timeTill != null || this.timeWait != null) {
			this.updateText();
		}
	}
}

function zeroIfEmpty(text: string) {
	return text.length == 0 ? '0' : text;
}